/**
 * content-builder-neo-fcrj.js — FCRJ behaviour layer on top of the Neo renderer.
 *
 * Loaded third (`content-builder-neo.js` → `-ext.js` → this file, all `defer`,
 * so document order is execution order — see snippets/aico-neo-content.liquid).
 * The engine and the ext file are kept diffable against the FC Lausanne-Sport
 * theme; anything FCRJ-only belongs here.
 *
 * WHAT IT DOES
 *
 * 1. Kalender-Abo .ics download buttons. Two ways in, same behaviour
 *    (loading label, download, inline error):
 *      - a native `theme-slot` element, `{slot: 'ics-herren'}` or
 *        `{slot: 'ics-frauen'}` (p23 decision D4) — the renderer paints the
 *        bare mount `<div class="neo-theme-slot" data-ls-slot="ics-herren">`
 *        (content-builder-neo-ext.js §15) and this file fills it with the
 *        button + hint + status line below, so the document carries no html
 *        element and no page CSS;
 *      - the legacy `html` element on page 11, which authors the same markup
 *        by hand (the engine's `html` renderer prints it verbatim — no
 *        sanitising, so `data-*` attributes and <button> survive). Both keep
 *        working side by side; the click handling is one delegated listener.
 *    The calendar itself is `assets/spielkalender-ics.js`
 *    (window.FCRJSpielkalender), shared with sections/spielkalender.liquid, and
 *    is fetched lazily — only pages that actually contain an ICS button pay
 *    for it. The button's look is theme-owned: assets/content-builder-neo-fcrj.css.
 *
 * 2. The squad UI mount. Both the Teams overview (templates/page.teams.json)
 *    and every team page (templates/page.aico-neo-team.json, one aiconeo page
 *    per CRM team — see snippets/team-pages.liquid) are an aico-neo-page
 *    section followed by the teams-overview section. The Neo document holds
 *    what the club edits (team name, team photo, intro text); the squad UI
 *    stays in sections/teams-overview.liquid and is fed by the aico API. One
 *    contract with those documents (scripts/aico-migrate/lib/team-page-neo.mjs
 *    and teams-neo.mjs):
 *
 *      a native `theme-slot` element, `{slot: 'squad'}` (p23 decision D4)
 *        → `<div class="neo-theme-slot" data-ls-slot="squad">`, or the legacy
 *        `<div class="fc-teams-app" data-fcrj-teams-app>…</div>` inside an
 *        `html` element. Either is the mount point: after the Neo render this
 *        file MOVES the whole teams-overview section node
 *        (`.section-teams-overview`) into it and adds `is-mounted`, so the
 *        roster sits where the editor placed the placeholder — under the photo
 *        and intro on a team page (its own listeners and element references
 *        survive a move). No placeholder → the section stays where the template
 *        put it. The legacy host keeps working until the last team page has
 *        been rebuilt natively.
 *
 *        This does NOT collide with the ext file's own slot mover: that one
 *        moves theme sections stamped `data-ls-slot-source="<name>"` and FCRJ
 *        stamps none, so it returns without touching anything. It also runs
 *        FIRST (this file wraps `Neo.hydrate` after the ext file did), and its
 *        late-placement signal `ls:theme-slots-placed` is listened to below in
 *        case that ever changes.
 *
 *    The team photos themselves are NOT read here: snippets/team-pages.liquid
 *    picks them out of each team page's inline neo metafield server-side
 *    (window.aicoTeamPhotos).
 *
 * BUTTON CONTRACT for page authors (put this in a Neo `html` element):
 *
 *   <button class="fc-btn fc-ics" type="button" data-fcrj-ics data-club-id="2"
 *     data-label-loading="Kalender wird erstellt …"
 *     data-error="Der Kalender konnte nicht erstellt werden. Bitte später erneut versuchen.">Kalender herunterladen (.ics)</button>
 *
 *   - data-fcrj-ics        (required, no value) marks the button.
 *   - data-club-id         (required) aico soccerClubId: 2 = Herren 1, 51 = Frauen 1.
 *   - data-label-loading   (optional) label while fetching; the original label
 *                          is restored afterwards.
 *   - data-error           (optional) message shown when the calendar cannot be
 *                          built. Written into the nearest following/sibling
 *                          `[data-fcrj-ics-status]` element if the author ships
 *                          one; otherwise logged only.
 *   - data-team-label      (optional) name used inside the .ics
 *                          (X-WR-CALNAME); defaults from data-club-id.
 *   - data-filename        (optional) download filename.
 *
 * Optional status line next to the button:
 *   <p data-fcrj-ics-status role="status" hidden></p>
 *
 * Clicks are handled by ONE delegated document listener, so buttons work no
 * matter which render path painted them (inline `#aico-page-data` or the >64KB
 * URL fallback) and after any re-render.
 */
(function (window, document) {
  'use strict';

  var ICS_SELECTOR = '[data-fcrj-ics][data-club-id]';

  /** aico soccerClubId → the name that goes into the .ics calendar title. */
  var TEAM_LABELS = {
    '2': 'Herren 1',
    '51': 'Frauen 1'
  };

  // ------------------------------------------------------------------
  // `theme-slot` → .ics button (p23 D4)
  //
  // The document holds nothing but the slot; the button, its hint and the
  // status line are theme markup, exactly as page 11 authored them by hand.
  // Copy is German and lives here because it IS the theme's button — an
  // author who wants other wording still has the html element.
  // ------------------------------------------------------------------

  var ICS_SLOT_SELECTOR = '[data-ls-slot="ics-herren"], [data-ls-slot="ics-frauen"]';

  var ICS_SLOTS = {
    'ics-herren': { clubId: '2', label: 'Herren 1' },
    'ics-frauen': { clubId: '51', label: 'Frauen 1' }
  };

  var ICS_LABEL = 'Kalender herunterladen (.ics)';
  var ICS_LABEL_LOADING = 'Kalender wird erstellt …';
  var ICS_HINT = 'Alle kommenden Spiele der laufenden Saison.';
  var ICS_ERROR = 'Der Kalender konnte nicht erstellt werden. Bitte später erneut versuchen.';
  var ICS_NOSCRIPT = 'Für den Kalender-Download wird JavaScript benötigt.';

  function icsSlotHtml(config) {
    return '<button class="fcrj-ics-btn" type="button" data-fcrj-ics' +
      ' data-club-id="' + config.clubId + '"' +
      ' data-team-label="' + config.label + '"' +
      ' data-label-loading="' + ICS_LABEL_LOADING + '"' +
      ' data-error="' + ICS_ERROR + '">' + ICS_LABEL + '</button>' +
      '<p class="fcrj-ics-hint">' + ICS_HINT + '</p>' +
      '<p class="fcrj-ics-status" data-fcrj-ics-status role="status" hidden></p>' +
      '<noscript><p class="fcrj-ics-hint">' + ICS_NOSCRIPT + '</p></noscript>';
  }

  /**
   * Fill every ICS theme slot the rendered document carries. Idempotent: a slot
   * that already holds its button (re-hydrate, theme editor reload) is skipped,
   * and a slot filled by hand in the document is left exactly as authored.
   */
  function fillIcsSlots(root) {
    var scope = (root && root.querySelectorAll) ? root : document;
    var slots = scope.querySelectorAll(ICS_SLOT_SELECTOR);
    for (var i = 0; i < slots.length; i++) {
      var slot = slots[i];
      if (slot.querySelector(ICS_SELECTOR)) continue;
      var config = ICS_SLOTS[slot.getAttribute('data-ls-slot')];
      if (!config) continue;
      slot.classList.add('fcrj-ics-slot');
      slot.innerHTML = icsSlotHtml(config);
    }
  }

  // ------------------------------------------------------------------
  // `theme-slot` → Google Maps embed (p23 D3, page 79 Anfahrt)
  //
  // There is no native map element and an `<iframe>` in the document would be
  // an html block again, so the document carries only the slot (with its own
  // per-tier height in the element's style bags) and the theme owns the embed.
  // The address is the club's ground; nothing else on the site embeds a map.
  // ------------------------------------------------------------------

  var MAP_SLOTS = {
    'map-anfahrt': {
      src: 'https://maps.google.com/maps?q=Sportanlage%20Gr%C3%BCnfeld%2C%208645%20Rapperswil-Jona&t=m&z=14&output=embed&iwloc=near',
      title: 'Karte: Sportanlage Grünfeld, 8645 Jona'
    }
  };

  function fillMapSlots(root) {
    var scope = (root && root.querySelectorAll) ? root : document;
    var slots = scope.querySelectorAll('[data-ls-slot^="map-"]');
    for (var i = 0; i < slots.length; i++) {
      var slot = slots[i];
      if (slot.querySelector('iframe')) continue;
      var config = MAP_SLOTS[slot.getAttribute('data-ls-slot')];
      if (!config) continue;
      slot.classList.add('fcrj-map-slot');
      slot.innerHTML = '<iframe src="' + config.src + '" title="' + config.title + '"' +
        ' loading="lazy" referrerpolicy="no-referrer-when-downgrade" allowfullscreen></iframe>';
    }
  }

  var modulePromise = null;

  /**
   * The module URL is stamped on the mount by snippets/aico-neo-content.liquid
   * (`asset_url`), because a theme asset lives on Shopify's CDN and cannot be
   * guessed from JS.
   */
  function moduleSrc() {
    var mount = document.getElementById('aico-content');
    return (mount && mount.getAttribute('data-fcrj-ics-src')) || '';
  }

  /** @returns {Promise<Object|null>} the module, or null when it cannot load. */
  function loadModule() {
    if (window.FCRJSpielkalender) return Promise.resolve(window.FCRJSpielkalender);
    if (modulePromise) return modulePromise;

    var src = moduleSrc();
    if (!src) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-fcrj: no data-fcrj-ics-src on #aico-content — .ics buttons stay inert');
      }
      return Promise.resolve(null);
    }

    modulePromise = new Promise(function (resolve) {
      var script = document.createElement('script');
      script.src = src;
      script.defer = true;
      script.onload = function () { resolve(window.FCRJSpielkalender || null); };
      script.onerror = function () { resolve(null); };
      document.head.appendChild(script);
    });
    return modulePromise;
  }

  /** Load the .ics module as soon as a page turns out to contain a button. */
  function prepare(root) {
    var scope = (root && root.querySelectorAll) ? root : document;
    if (scope.querySelector(ICS_SELECTOR)) loadModule();
  }

  function statusEl(button) {
    var parent = button.parentNode;
    var box = parent && parent.querySelector && parent.querySelector('[data-fcrj-ics-status]');
    if (box) return box;
    // Authors sometimes put the status line outside the button's own wrapper.
    var container = button.closest && button.closest('.content-type-neo-html, .content-type-html');
    return (container && container.querySelector('[data-fcrj-ics-status]')) || null;
  }

  function showStatus(button, message) {
    var box = statusEl(button);
    if (!box) {
      if (window.console && console.warn) console.warn('content-builder-neo-fcrj: ' + message);
      return;
    }
    box.textContent = message;
    box.hidden = false;
    box.removeAttribute('hidden');
  }

  function clearStatus(button) {
    var box = statusEl(button);
    if (!box) return;
    box.textContent = '';
    box.hidden = true;
  }

  function errorMessage(button) {
    return button.getAttribute('data-error') ||
      'Der Kalender konnte nicht erstellt werden. Bitte später erneut versuchen.';
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    var button = target && target.closest && target.closest(ICS_SELECTOR);
    if (!button || button.disabled) return;

    // Works on <a href="#…"> too, so a document can author a link instead.
    event.preventDefault();

    var clubId = button.getAttribute('data-club-id');
    var teamLabel = button.getAttribute('data-team-label') || TEAM_LABELS[String(clubId)] || '';
    var filename = button.getAttribute('data-filename') ||
      ('fcrj-' + (teamLabel ? teamLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-') : clubId) + '-spielplan.ics');
    var loadingLabel = button.getAttribute('data-label-loading');
    var idleLabel = button.textContent;

    clearStatus(button);

    function restore() {
      button.disabled = false;
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
      if (loadingLabel) button.textContent = idleLabel;
    }

    button.disabled = true;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    if (loadingLabel) button.textContent = loadingLabel;

    // The .ics module is lazy-loaded; a click that beats the download simply
    // waits for it (the loading state is already showing).
    loadModule()
      .then(function (ics) {
        if (!ics) return { ok: false, reason: 'module' };
        return ics.downloadIcs(clubId, { teamLabel: teamLabel, filename: filename });
      })
      .then(function (result) {
        if (result && result.ok) return;
        showStatus(button, errorMessage(button));
      })
      .catch(function (error) {
        if (window.console && console.warn) {
          console.warn('content-builder-neo-fcrj: .ics download failed', error);
        }
        showStatus(button, errorMessage(button));
      })
      .finally(restore);
  });

  // ------------------------------------------------------------------
  // Mount the squad UI into the document's placeholder
  // ------------------------------------------------------------------

  // The native `theme-slot {slot:'squad'}` (p23 D4) first, the legacy html
  // placeholder second — a document rebuilt natively carries the slot, one that
  // has not been rebuilt yet carries the div. Both may exist while a page is
  // being migrated; the first in document order wins.
  var TEAMS_HOST_SELECTOR = '[data-fcrj-teams-app], [data-ls-slot="squad"]';
  var TEAMS_SECTION_SELECTOR = '.section-teams-overview';

  /** Move the teams-overview section into the authored placeholder (once). */
  function mountTeamsApp(root) {
    var scope = (root && root.querySelector) ? root : document;
    var host = scope.querySelector(TEAMS_HOST_SELECTOR) || document.querySelector(TEAMS_HOST_SELECTOR);
    if (!host || host.getAttribute('data-fcrj-mounted') === '1') return;
    var section = document.querySelector(TEAMS_SECTION_SELECTOR);
    if (!section || host.contains(section)) return;
    host.appendChild(section);
    host.setAttribute('data-fcrj-mounted', '1');
    // `fc-teams-app` too: teams.css keys the phone reveal (≤749px, the CLS
    // guard) on `.fc-teams-app.is-mounted`, and a native `theme-slot` host
    // carries only `neo-theme-slot`. Without it the squad stayed hidden on phones.
    host.classList.add('fc-teams-app', 'is-mounted');
  }

  // ------------------------------------------------------------------
  // Mobile: load the images the phone actually sees first, eagerly
  // ------------------------------------------------------------------

  var MOBILE_QUERY = '(max-width: 749px)';

  /**
   * Both emitters print every Neo image `loading="lazy"` with no dimensions
   * (content-builder-neo.js renderImage, -ext.js tiles/logos/features/…), so on
   * a phone the images at the top of a freshly mounted document only start
   * downloading once the lazy-load heuristic runs — the first viewport paints
   * empty and fills in afterwards (/pages/fcrj-camps, /pages/our-sponsors).
   *
   * The document is already in the DOM when this runs (both render paths do
   * `mount.innerHTML = …` and then hydrate), so the rects are real: whatever
   * intersects the first viewport is flipped to an eager, high-priority fetch.
   * Everything below the fold keeps its lazy loading. Desktop is untouched —
   * the whole pass is behind the same 749px query the CSS uses.
   */
  function eagerFirstViewportImages() {
    if (!window.matchMedia || !window.matchMedia(MOBILE_QUERY).matches) return;
    var mount = document.getElementById('aico-content');
    if (!mount) return;

    var images = mount.querySelectorAll('img[loading="lazy"]');
    var viewportHeight = window.innerHeight || 0;
    var viewportWidth = window.innerWidth || 0;

    for (var i = 0; i < images.length; i++) {
      var rect = images[i].getBoundingClientRect();
      var inViewport = rect.top < viewportHeight && rect.bottom > 0 &&
        rect.left < viewportWidth && rect.right > 0;
      if (!inViewport) continue;
      images[i].setAttribute('fetchpriority', 'high');
      // Set last: flipping `loading` is what actually starts the download.
      images[i].setAttribute('loading', 'eager');
    }
  }

  // Both render paths funnel through Neo.hydrate; wrap it so the module is
  // requested as soon as the buttons exist and the squad UI is mounted as soon
  // as the document is painted. The inline-data path may already have hydrated
  // before this file ran (the ext file repaints on load), so also scan once
  // right now.
  var Neo = window.NewsNeo;
  if (Neo && typeof Neo.hydrate === 'function') {
    var originalHydrate = Neo.hydrate;
    Neo.hydrate = function (root) {
      originalHydrate(root);
      try {
        fillIcsSlots(root);
        fillMapSlots(root);
        prepare(root);
      } catch (error) {
        if (window.console && console.warn) {
          console.warn('content-builder-neo-fcrj: hydration failed', error);
        }
      }
      try {
        mountTeamsApp(root);
      } catch (error) {
        if (window.console && console.warn) {
          console.warn('content-builder-neo-fcrj: teams mount failed', error);
        }
      }
      try {
        eagerFirstViewportImages();
      } catch (error) {
        if (window.console && console.warn) {
          console.warn('content-builder-neo-fcrj: image priming failed', error);
        }
      }
    };
  }
  fillIcsSlots(document);
  fillMapSlots(document);
  prepare(document);
  mountTeamsApp(document);
  eagerFirstViewportImages();

  // If the ext file ever moves a theme section into a slot AFTER this file has
  // run (it only does that for sections stamped `data-ls-slot-source`, of which
  // FCRJ has none today), the squad placeholder may have moved with it — mount
  // again. Both calls are idempotent.
  document.addEventListener('ls:theme-slots-placed', function () {
    try {
      fillIcsSlots(document);
      mountTeamsApp(document);
    } catch (error) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-fcrj: post-slot mount failed', error);
      }
    }
  });

  // Safety net for the mobile-only "hide the squad until mounted" rule in
  // teams.css (.teams-overview--await-mount, stamped by teams-overview.liquid):
  // if the Neo document never arrives (fetch error, no placeholder), reveal the
  // squad anyway so a phone never ends up with an empty team page.
  setTimeout(function () {
    var pending = document.querySelectorAll('.teams-overview--await-mount');
    for (var i = 0; i < pending.length; i++) {
      pending[i].classList.add('is-revealed');
    }
  }, 8000);

  // Safety net for the two mobile-only reserves in content-builder-neo.css
  // (`#aico-content:empty` keeps a screen free for the document, an empty
  // `.aico-form-slot` keeps 36rem free for a linked form): if the fetch never
  // answers, collapse them instead of leaving a phone staring at a blank
  // screen. The class is only ever *read* by `:empty` rules, so it does
  // nothing on a page that loaded normally — and nothing at all above 749px.
  // 15s, not the 8s above: on a throttled 3G phone the >64KB fallback path
  // legitimately paints at 10–13s, and collapsing the reserve before that
  // would recreate the very jump it prevents.
  setTimeout(function () {
    var mount = document.getElementById('aico-content');
    if (mount) mount.classList.add('neo-mount-timeout');
  }, 15000);
})(window, document);
